/**
 * One autofilled field with an Automatisk / Manuell switch. Automatic shows
 * the text generated from the linked source (parties, properties); manual
 * turns it into free text, pre-filled with the generated text, that later
 * source changes leave alone. Switching back regenerates it.
 *
 * Presentational: the caller persists the mode switch (legacy
 * `contract_properties.set_mode`, beta `contracts.set_autofill_mode`).
 */

export type AutofillMode = 'auto' | 'manual';

export function AutofillModeField({
  id,
  label,
  mode,
  text,
  required,
  missing,
  multiline = true,
  disabled,
  modeLocked,
  busy,
  emptyAutoText,
  autoHelp,
  manualHelp,
  onSwitch,
  onTextChange,
}: {
  id: string;
  label: string;
  mode: AutofillMode;
  text: string;
  required?: boolean;
  /** Required and empty — draws the label in the missing colour. */
  missing?: boolean;
  multiline?: boolean;
  disabled: boolean;
  /** Mode switching is draft-only server-side. */
  modeLocked: boolean;
  busy: boolean;
  emptyAutoText: string;
  autoHelp: string;
  manualHelp: string;
  onSwitch: (mode: AutofillMode) => void;
  onTextChange: (next: string) => void;
}) {
  const inputCls =
    'w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm shadow-sm focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900 disabled:bg-stone-100';
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <label
          htmlFor={id}
          className={'text-xs font-medium ' + (missing ? 'text-red-700' : 'text-stone-700')}
        >
          {label}
          {required && <span className="ml-0.5 text-red-600">*</span>}
        </label>
        <div className="inline-flex shrink-0 overflow-hidden rounded border border-stone-300 text-[11px]">
          {(['auto', 'manual'] as const).map((m) => (
            <button
              key={m}
              type="button"
              disabled={disabled || modeLocked || busy || mode === m}
              onClick={() => onSwitch(m)}
              className={
                'px-2 py-0.5 transition-colors ' +
                (mode === m
                  ? 'bg-stone-900 text-white'
                  : 'bg-white text-stone-600 hover:bg-stone-50 disabled:opacity-50')
              }
            >
              {m === 'auto' ? 'Automatisk' : 'Manuell'}
            </button>
          ))}
        </div>
      </div>
      {mode === 'manual' ? (
        multiline ? (
          <textarea
            id={id}
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            disabled={disabled || busy}
            rows={Math.min(8, Math.max(3, text.split('\n').length + 1))}
            className={inputCls}
          />
        ) : (
          <input
            id={id}
            type="text"
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            disabled={disabled || busy}
            className={inputCls}
          />
        )
      ) : (
        <div
          id={id}
          className="min-h-[2.25rem] whitespace-pre-line rounded border border-stone-200 bg-stone-50 px-2 py-1.5 text-sm text-stone-700"
        >
          {text || <span className="text-stone-400">{emptyAutoText}</span>}
        </div>
      )}
      <p className="text-[11px] text-stone-500">{mode === 'manual' ? manualHelp : autoHelp}</p>
    </div>
  );
}
